import { useSyncExternalStore } from 'react';
import { Appointment, ManualReminderResponse, PaginatedResponse } from '@/types';
import { api } from '@/services/api';
import { useStaffDataContext } from './StaffDataContext';

export interface AppointmentListParams {
  page?: number | undefined;
  pageSize?: number;
  status?: string;
  venueId?: string;
  serviceId?: string;
  companyId?: string;
  dateFrom?: string;
  dateTo?: string;
  paymentMethod?: string;
  paymentStatus?: string;
  poNumber?: string;
  customerName?: string;
  q?: string;
  sortBy?: 'date' | 'createdAt';
  sortDir?: 'asc' | 'desc';
}

const buildQuery = (params: AppointmentListParams) => ({
  page: params.page,
  pageSize: params.pageSize,
  status: params.status || undefined,
  venueId: params.venueId || undefined,
  serviceId: params.serviceId || undefined,
  companyId: params.companyId || undefined,
  dateFrom: params.dateFrom || undefined,
  dateTo: params.dateTo || undefined,
  paymentMethod: params.paymentMethod || undefined,
  paymentStatus: params.paymentStatus || undefined,
  poNumber: params.poNumber || undefined,
  customerName: params.customerName || undefined,
  q: params.q || undefined,
  sortBy: params.sortBy || undefined,
  sortDir: params.sortDir || undefined
});

export const useAppointments = (params: AppointmentListParams): PaginatedResponse<Appointment> => {
  const ctx = useStaffDataContext();
  useSyncExternalStore(
    (cb) => ctx.subscribe('appointments', cb),
    () => 0,
    () => 0
  );
  return ctx.read('appointments', params, (p: AppointmentListParams) =>
    api.listStaffBookings(buildQuery(p))
  );
};

export interface AppointmentMutations {
  create: (appointment: Appointment) => Promise<void>;
  update: (appointment: Appointment, notify: { email: boolean; sms: boolean }) => Promise<void>;
  updateStatus: (
    id: string,
    status: 'completed' | 'booked' | 'noshow' | 'cancelled'
  ) => Promise<void>;
  remove: (id: string) => Promise<void>;
  sendReminder: (
    appointmentId: string,
    type: 'sms' | 'email' | 'form',
    templateType?: string
  ) => Promise<ManualReminderResponse>;
  sendQuestionnaire: (appointmentId: string) => Promise<{ success: boolean }>;
  /** Force appointment lists to refetch after an out-of-band change (e.g. a certificate issued). */
  invalidate: () => void;
}

export const useAppointmentMutations = (): AppointmentMutations => {
  const ctx = useStaffDataContext();
  const after = () => ctx.invalidate('appointments');
  return {
    create: async (appointment) => {
      await api.createBooking(appointment);
      after();
    },
    update: async (appointment, notify) => {
      await api.updateBooking(appointment, notify);
      after();
    },
    updateStatus: async (id, status) => {
      await api.updateBookingStatus(id, status);
      after();
    },
    remove: async (id) => {
      await api.deleteBooking(id);
      after();
    },
    sendReminder: async (appointmentId, type, templateType) => {
      const result = await api.triggerReminder(appointmentId, type, templateType);
      after();
      return result;
    },
    sendQuestionnaire: async (appointmentId) => {
      const result = await api.sendQuestionnaire(appointmentId);
      after();
      return result;
    },
    invalidate: after
  };
};
